import Avatar from '@mui/material/Avatar'
import { useTheme } from '@mui/material/styles'
import Tooltip from '@mui/material/Tooltip'
import React from 'react'

import { getWorkOrderStatusDisplayLabel } from 'settings/constants/service'
import { ALPHA_38_PERCENT } from 'theme/colors'

import { getWorkOrderStatusIcon, hasSplitStatusIcon } from './workOrderStatusIcon'

type Props = {
  status?: string
  size?: number
}

// Icon-only sibling of WorkOrderStatusChip — same inner avatar (translucent black + white
// glyph) but standalone, for tight contexts (per-container row) where the full chip with
// label is too wide and overflows the other chips.
const WorkOrderStatusBadge: React.FC<Props> = ({ status: rawStatus, size = 24 }) => {
  const theme = useTheme()
  // Same as the chip: the raw column can hold a roll-up, and the glyph has to follow the label.
  const status = getWorkOrderStatusDisplayLabel(rawStatus, rawStatus)

  const iconSize = hasSplitStatusIcon(status) ? size : Math.round(size * 0.6)
  const icon = getWorkOrderStatusIcon(status, { color: 'background.paper', width: iconSize, height: iconSize })
  // A status with no glyph gets no badge at all — an empty avatar reads as a status of its own.
  if (!icon) return null

  const bg = `${theme.palette.background.black}${ALPHA_38_PERCENT}`
  // Glyph-only by design, so the name has to reach the user through the tooltip and aria-label.
  return (
    <Tooltip arrow disableInteractive title={status}>
      <Avatar
        aria-label={status}
        sx={{ width: size, height: size, bgcolor: hasSplitStatusIcon(status) ? 'transparent' : bg, flexShrink: 0 }}
      >
        {icon}
      </Avatar>
    </Tooltip>
  )
}

export default WorkOrderStatusBadge
